import { defineStore, acceptHMRUpdate } from 'pinia';
import { DELIVERY_METHODS, SURVEY_DISPLAY_ACRONYMS, SURVEY_TYPES } from '@/surveys/constants/surveys.constants';
import * as api from '@/stores/api/surveys.api';
import { runAggregationOnce } from '@utils/aggregation';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import SurveyItemsAllAggregation from '@/surveys/aggregations/survey-items/survey-items-all.aggregation';

interface SurveyDeliveryMethod {
    deliveryType?: string;
    [key: string]: unknown;
}

interface Survey {
    id: string;
    name?: string;
    items?: unknown[];
    delivery?: SurveyDeliveryMethod[];
    [key: string]: unknown;
}

interface SurveysState {
    surveys: Record<string, Survey>;
    surveyItems: unknown[];
    unconfirmedEmailDeliveryIds: Record<string, boolean>;
}

// Module-level promise deduplication keyed by operation
const promises: Record<string, Promise<unknown> | null> = {};
const loadOneSeqById: Record<string, number> = {};
let loadAllItemsSeq = 0;

function loadOneKey (id: string) {
    return `loadOne-${id}`;
}

function invalidateLoadOne (id: string) {
    loadOneSeqById[id] = (loadOneSeqById[id] ?? 0) + 1;
    // Drop the cached promise too, or later callers dedup onto a read of pre-mutation data.
    promises[loadOneKey(id)] = null;
}

export function __resetPromisesForUnitTests () {
    for (const key of Object.keys(promises)) {
        promises[key] = null;
    }
    for (const key of Object.keys(loadOneSeqById)) {
        delete loadOneSeqById[key];
    }
    loadAllItemsSeq = 0;
}

export function getInitialState (): SurveysState {
    return {
        surveys: {},
        surveyItems: [],
        unconfirmedEmailDeliveryIds: {},
    };
}

export const useSurveysStore = defineStore('surveys', {
    state: getInitialState,
    getters: {
        list: (state) => {
            if (hasSegmentFlag('npsOnSentiment')) {
                return state.surveys;
            }

            return Object.fromEntries(
                Object.entries(state.surveys).filter(([, survey]) => survey.surveyType !== SURVEY_TYPES.NET_PROMOTER_SCORE),
            );
        },
        listItems: (state) => state.surveyItems,
        surveyById: (state) => (id: string): Survey | undefined => state.surveys[id],
        // The marker is only ever set for email the server has and the cache might not, so a cached
        // survey already showing email has caught up — whichever path put it there. loadOne's write
        // is not the only one: loadAll replaces the map wholesale and never matches its guard.
        hasUnconfirmedEmailDelivery: (state) => (id: string): boolean =>
            !!state.unconfirmedEmailDeliveryIds[id] &&
            !state.surveys[id]?.delivery?.some((method) => method.deliveryType === DELIVERY_METHODS.EMAIL),
    },
    actions: {
        async loadAll () {
            if (promises.loadAll) {
                return promises.loadAll;
            }

            const loadAllPromise = api
                .fetchSurveys()
                .then((surveys: Record<string, Survey>) => {
                    this.surveys = surveys;

                    return surveys;
                })
                .finally(() => {
                    promises.loadAll = null;
                });

            promises.loadAll = loadAllPromise;

            return loadAllPromise;
        },
        async loadOne ({ id, reFetch = false }: { id: string; reFetch?: boolean }) {
            const key = loadOneKey(id);
            if (!reFetch && promises[key]) {
                return promises[key];
            }

            const seq = (loadOneSeqById[id] ?? 0) + 1;
            loadOneSeqById[id] = seq;
            const cachedBeforeFetch = this.surveys[id];

            const loadOnePromise = api
                .fetchSurvey(id)
                .then((survey: Survey) => {
                    if (loadOneSeqById[id] === seq && this.surveys[id] === cachedBeforeFetch) {
                        this.surveys[id] = survey;
                        delete this.unconfirmedEmailDeliveryIds[id];
                    }

                    return survey;
                })
                .finally(() => {
                    if (promises[key] === loadOnePromise) {
                        promises[key] = null;
                    }
                });

            promises[key] = loadOnePromise;

            return loadOnePromise;
        },
        async loadAllItems ({ reFetch = false }: { reFetch?: boolean } = {}): Promise<unknown> {
            if (promises.loadAllItems && !reFetch) {
                return promises.loadAllItems;
            }

            loadAllItemsSeq += 1;
            const seq = loadAllItemsSeq;
            const loadItemsPromise = runAggregationOnce(SurveyItemsAllAggregation, {})
                .then((surveyItems: unknown) => {
                    // A save or refetch superseded this load, so its callers wait for the newer one
                    if (seq !== loadAllItemsSeq) {
                        return this.loadAllItems();
                    }
                    this.surveyItems = surveyItems as unknown[];

                    return surveyItems;
                })
                .catch((error: unknown) => {
                    if (promises.loadAllItems === loadItemsPromise) {
                        promises.loadAllItems = null;
                    }
                    throw error;
                });
            promises.loadAllItems = loadItemsPromise;

            return loadItemsPromise;
        },
        invalidateAllItems () {
            loadAllItemsSeq += 1;
            promises.loadAllItems = null;
        },
        async createFromTemplate ({ type, productName, appId, productAreaIds, deliveryMethods, guideLayout, hideThankYou, snoozeEnabled }: {
            type: string;
            productName: string;
            appId: string;
            productAreaIds: string[];
            deliveryMethods: unknown;
            guideLayout?: string;
            hideThankYou?: boolean;
            snoozeEnabled?: boolean;
        }) {
            const payload = {
                surveyType: type,
                surveyData: { productName },
                name: `${SURVEY_DISPLAY_ACRONYMS[type as keyof typeof SURVEY_DISPLAY_ACRONYMS]} Survey for ${productName}`,
                appIds: [appId],
                productAreaIds,
                delivery: deliveryMethods,
                hideThankYou,
                singleStep: guideLayout && guideLayout !== 'MULTI_STEP',
                snoozeEnabled,
            };

            const res = await api.createSurveyFromTemplate({ payload });
            const survey = { ...res.survey, items: res.surveyItems };
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateStatus ({ id, status }: { id: string; status: string }) {
            const survey = await api.updateSurveyStatus({ id, status });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateSurveyName ({ id, name }: { id: string; name: string }) {
            const rollbackSurveyState = this.surveyById(id);
            this.surveys[id] = { ...this.surveys[id], name };

            try {
                const updatedSurvey = await api.updateSurveyDetails({ id, payload: { name } });
                this.surveys[id] = updatedSurvey;

                return updatedSurvey;
            } catch (error) {
                if (rollbackSurveyState) {
                    this.surveys[id] = rollbackSurveyState;
                }
                throw error;
            }
        },
        async updateSurveyProductAreaId ({ id, productAreaIds }: { id: string; productAreaIds: string[] }) {
            const survey = await api.updateSurveyDetails({ id, payload: { productAreaIds } });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateSurveySnoozeEnabled ({ id, snoozeEnabled, snoozeDurationDays }: { id: string; snoozeEnabled: boolean; snoozeDurationDays?: number }) {
            const survey = await api.updateSurveyDetails({ id, payload: { snoozeEnabled, snoozeDurationDays } });
            this.surveys[survey.id] = { ...this.surveys[survey.id], ...survey };

            return survey;
        },
        async updateDelivery ({ id, audienceConfig, deliveryId }: { id: string; audienceConfig: unknown; deliveryId: string }) {
            const survey = await api.updateSurveyDelivery({ id, audienceConfig, deliveryId });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateDeliveryItems ({ id, deliveryId, deliveryConfig }: { id: string; deliveryId: string; deliveryConfig: unknown }) {
            const survey = await api.updateSurveyDeliveryItems({ id, deliveryId, deliveryConfig });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async upsertEmailDelivery ({ id, payload }: { id: string; payload: unknown }) {
            const survey = await api.upsertSurveyEmailDelivery({ id, payload });
            invalidateLoadOne(id);
            this.surveys[survey.id] = survey;
            delete this.unconfirmedEmailDeliveryIds[survey.id];

            return survey;
        },
        async publishEmailDelivery ({ id, payload }: { id: string; payload: { emailProfileId: string; subject: string } }) {
            const response = await api.publishSurveyEmailDelivery({ id, payload });
            try {
                await this.loadOne({ id, reFetch: true });
            } catch (error) {
                // The POST returns only { action }, so this reload is the only way the store learns
                // the survey now has email. Publishing did succeed, so resolve — but record that
                // the cache is behind, or readers render email as off against a live campaign.
                console.error(error);
                this.markEmailDeliveryUnconfirmed(id);
            }

            return response;
        },
        // Callers that can read the survey's delivery shape use this for the case a failed reload
        // does not cover: loadOne resolving without applying, when a concurrent write replaced the
        // cached record. Keeping the check out here spares the store the survey utils' import graph.
        markEmailDeliveryUnconfirmed (id: string) {
            this.unconfirmedEmailDeliveryIds[id] = true;
        },
        async removeEmailDelivery ({ id }: { id: string }) {
            const survey = await api.deleteSurveyEmailDelivery({ id });
            invalidateLoadOne(id);
            this.surveys[survey.id] = survey;
            delete this.unconfirmedEmailDeliveryIds[survey.id];

            return survey;
        },
        async updateSurveyRecurrence (params: { id: string; recurrence: unknown }) {
            const survey = await api.updateSurveyRecurrence(params);
            this.surveys[survey.id] = survey;

            return survey;
        },
        async delete ({ id }: { id: string }) {
            await api.deleteSurvey(id);
            delete this.surveys[id];
        },
        async cloneSurvey ({ surveyId, targetAppId }: { surveyId: string; targetAppId: string }) {
            const res = await api.cloneSurvey({ surveyId, targetAppId });
            const survey = { ...res.survey, items: res.surveyItems };
            this.surveys[survey.id] = survey;

            return survey;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSurveysStore, module.hot));
}
